// backoffice.auth.login (legacy LoginView in AlternateLayout, shell-auth-dashboard.md §1): "WELCOME!" / "Sign in to
// Radish", Email and Password (eye toggle), Remember Me, "Log in", "Forgot Password?". Both fields are required with
// the legacy sentences; a wrong password answers the server's legacy sentence. Passkeys are offered beside the
// password (doc 11), and a locked email says how long to wait. There is no token sign-in in any build: the dev stack
// and the journeys sign in with the seeded users' passwords like everyone else.
// An account the cutover has not moved yet answers 403 `on_legacy` (migration plan �4.13, platform #240): the page says
// so and links to the current Radish back office's sign-in instead of showing an error.
import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { SignedIn } from '@radish/contracts';
import { AuthLayout, Banner, Button, Checkbox, TextField } from '@radish/ui-web';
import { useApi } from '../../data/api.tsx';
import { signIn } from '../../session/session.ts';
import { AuthCard, AuthHeading, FormProblem, PasswordField } from './fields.tsx';
import { EMAIL_REQUIRED } from '../../lib/email.ts';

const REMEMBER_KEY = 'radish.backoffice.rememberMe';

const remembered = (): boolean => {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) === 'true';
  } catch {
    return false;
  }
};

/** The current (legacy) Radish back office's sign-in: legacy `APP_FRONTEND_URL` + its `/login` route (dashboard.parsenext.com redirects here). */
const LEGACY_SIGN_IN_URL = 'https://dashboard.getradish.com/login';

const waitSentence = (seconds: number | undefined): string =>
  seconds === undefined ? 'Too many attempts. Please wait and try again.' : `Too many attempts. Try again in ${Math.max(1, Math.ceil(seconds / 60))} minute${seconds > 60 ? 's' : ''}.`;

export function LoginPage({ expired, reset = false }: { readonly expired: boolean; readonly reset?: boolean }) {
  const navigate = useNavigate();
  const { auth } = useApi();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(remembered);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [problem, setProblem] = useState<string | null>(expired ? 'Your session has expired. Please log in again.' : null);
  const [busy, setBusy] = useState(false);
  const [onLegacy, setOnLegacy] = useState(false);

  /** Shows why a sign-in failed: the account still on the current Radish is a notice with its link, not an error. */
  const failed = (error: { readonly code: string; readonly message: string; readonly retryAfterSeconds?: number }) => {
    setOnLegacy(error.code === 'on_legacy');
    if (error.code === 'on_legacy') return setProblem(null);
    return setProblem(error.code === 'too_many_attempts' ? waitSentence(error.retryAfterSeconds) : error.message);
  };

  const landed = async (signed: SignedIn) => {
    try {
      window.localStorage.setItem(REMEMBER_KEY, String(rememberMe));
    } catch {
      // Remember Me is a convenience; a browser without storage just asks again.
    }
    signIn(signed);
    // Staff and resellers pick an account (legacy lands them on /accounts); everyone else opens their location.
    await navigate({ to: signed.session.current === null ? '/accounts' : '/' });
  };

  const submit = async () => {
    const next = { ...(email.trim() === '' ? { email: EMAIL_REQUIRED } : {}), ...(password === '' ? { password: 'Please enter password' } : {}) };
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setProblem(null);
    setOnLegacy(false);
    const result = await auth.signIn({ email: email.trim(), password, rememberMe });
    setBusy(false);
    if (result.ok) return landed(result.data);
    return failed(result.error);
  };

  const withPasskey = async () => {
    setProblem(null);
    setOnLegacy(false);
    const options = await auth.passkeySignInOptions();
    if (!options.ok) return setProblem(options.error.message);
    let response: unknown;
    try {
      response = await startAuthentication({ optionsJSON: options.data.options as never });
    } catch {
      return setProblem('The passkey sign-in was cancelled.');
    }
    const result = await auth.passkeySignIn({ response, rememberMe });
    if (result.ok) return landed(result.data);
    return failed(result.error);
  };

  return (
    <AuthLayout logoSrc="/radish-logo.svg" darkLogoSrc="/radish-logo-dark.svg">
      <AuthCard bordered testId="login-card">
        <AuthHeading eyebrow="Welcome!" title="Sign in to Radish" />
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="flex flex-col gap-4"
          data-testid="login-form"
        >
          <FormProblem message={problem} />
          {onLegacy ? (
            <div role="alert" data-testid="auth-on-legacy">
              <Banner tone="info">
                Your account is still on the current Radish. Sign in there until your account moves to the new one.{' '}
                <a href={LEGACY_SIGN_IN_URL} className="font-medium text-primary underline">Sign in to the current Radish</a>
              </Banner>
            </div>
          ) : null}
          <TextField label="Email" type="email" value={email} onValueChange={setEmail} error={errors.email} autoComplete="username" />
          <PasswordField label="Password" value={password} onValueChange={setPassword} error={errors.password} autoComplete="current-password" />
          {/* Legacy's row: Quasar's 40 px checkbox target, "Remember Me" in the body grey, the link in blue; 12 px above Log in. */}
          <div className="flex h-10 items-center justify-between" data-testid="remember-row">
            <label className="flex cursor-pointer items-center text-[14px] leading-[21px] text-text-2">
              <span className="flex size-10 items-center justify-center">
                <Checkbox hideLabel label="Remember Me" checked={rememberMe} onCheckedChange={setRememberMe} />
              </span>
              <span aria-hidden>Remember Me</span>
            </label>
            <Link to="/forgot-password" className="text-[14px] leading-[21px] font-medium text-secondary hover:underline">Forgot Password?</Link>
          </div>
          <Button type="submit" variant="auth" loading={busy} className="-mt-1!">Log in</Button>
          <Button variant="outline" full icon={<KeyRound size={18} aria-hidden />} onClick={() => void withPasskey()} data-testid="passkey-sign-in">
            Sign in with a passkey
          </Button>
        </form>
        {reset ? <div className="mt-6" data-testid="password-was-reset"><Banner tone="info">Your password was reset. Log in with the new one.</Banner></div> : null}
      </AuthCard>
    </AuthLayout>
  );
}
